import { useEffect, useState } from "react";
import { CFormInput } from "@coreui/react";
import { Button } from "react-bootstrap";
import { getToastStore } from "@utils/ToastStore";
import { useLocation, useParams } from "react-router-dom";
import { t } from 'i18next';
import { useNavigate } from 'react-router';
import { useSpinner } from '@webkit/components/typescript/SpinnerContext';
import contactService from "@services/contact.service";
import { Contact } from "@models/contact.model";
import { errorToast, successToast } from "@webkit/functions/utils";

export default function EditCardContactPage() {

    const baseForm = {
        whatsapp: '',
        telephoneNumber: '',
        mobileNumber: '',
        email: '',
        website: '',
        airbnb: '',
        booking: '',
        facebook: '',
        instagram: '',
        youtube: '',
        tiktok: '',
        otherWebsites: []
    }


    const ctxToast = getToastStore();
    const location = useLocation();
    const navigate = useNavigate();
    const { showSpinner, hideSpinner } = useSpinner();
    const { cardId, parentId } = useParams();

    const [data, setData] = useState(baseForm)
    const [rows, setRows] = useState<[string, string][]>([]);

    useEffect(() => {
        const fetchdata = async () => {
            try {
                showSpinner();
                if (!parentId) return;
                const res = await contactService.getContactInfo(Number.parseInt(parentId));
                setData(res);
                setRows(Object.entries(res.otherWebsites));
            } catch (error) {
                errorToast(ctxToast, t("error.unable_to_fetch"));
            } finally {
                hideSpinner();
            }
        }

        fetchdata();
    }, [])

    function handleContactChange(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
        let value = event.target.value;
        setData((data) => {
            return {
                ...data,
                [event.target.name]: value,
            };
        });
    }

    const handleOtherWebsiteChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number,
        field: "key" | "value"
    ) => {
        const newRows = [...rows];

        if (field === "key") {
            newRows[index] = [e.target.value, newRows[index][1]];
        } else {
            newRows[index] = [newRows[index][0], e.target.value];
        }

        setRows(newRows);
    };

    const handleSave = async () => {
        const phoneRegex = /^\+?[0-9]{10,15}$/;
        try {
            showSpinner();
            if (!parentId) return;

            for (let i = 0; i < rows.length; i++) {
                const [key, value] = rows[i];

                if (((key && !value) || (!key && value)) || (key.trim() === "" || value.trim() === "")) {
                    errorToast(ctxToast, t("error.complete_both_fields") + ` ${i + 1}`);
                    hideSpinner();
                    return; 
                }
            }

            if(!phoneRegex.test(data.whatsapp.replace(/[\s\-()]/g, ''))){
                errorToast(ctxToast, t("error.wrong_whatsapp_number"));
                hideSpinner();
                return; 
            }
            if(!phoneRegex.test(data.mobileNumber.replace(/[\s\-()]/g, ''))){
                errorToast(ctxToast, t("error.wrong_mobile_number"));
                hideSpinner();
                return; 
            }
            
            if(!!data.telephoneNumber && data.telephoneNumber.length > 0){
                if(!phoneRegex.test(data.telephoneNumber.replace(/[\s\-()]/g, ''))){
                    errorToast(ctxToast, t("error.wrong_telephone_number"));
                    hideSpinner();
                    return; 
                }
            }
            

            const obj = Object.fromEntries(rows);

            const contactDto = new Contact(
                data.whatsapp.trim(),
                data.telephoneNumber.trim(),
                data.mobileNumber.trim(),
                data.email.trim(),
                data.website.trim(),
                data.airbnb.trim(),
                data.booking.trim(),
                data.facebook.trim(),
                data.instagram.trim(),
                data.youtube.trim(),
                data.tiktok.trim(),
                obj
            );

            await contactService.updateContactInfo(
                Number.parseInt(parentId),
                contactDto
            );

            successToast(ctxToast, t("success.contact_info_updated"));
            navigate(-1);

        } catch (error) {
            errorToast(ctxToast, t("error.unable_to_update_contact_info"));
        } finally {
            hideSpinner();
        }
    };


    const handleAddRow = () => {
        if (rows.length >= 10) return;
        setRows([...rows, ["", ""]]);
    };
    const handleRemoveRow = (index: number) => {
        setRows(prevRows => prevRows.filter((_, i) => i !== index));
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="card shadow-lg" style={{ width: '60%', borderRadius: '25px' }}>
                <div
                    className="hst-card-header card-header text-uppercase d-flex align-items-center"
                    style={{ justifyContent: 'center', position: 'relative' }}
                >
                    <div>{t('title.edit_cardContact')}</div>

                    <div
                        className="d-flex align-items-center"
                        style={{
                            position: 'absolute',
                            right: '2rem',
                            top: '50%',
                            transform: 'translateY(-50%)'
                        }}
                    >
                    </div>
                </div>

                <div className="row mt-5 mx-4">
                    <div className="col-12 col-md-6">
                        <CFormInput
                            name="whatsapp"
                            type="text"
                            className="contacts-inputs"
                            label={`${t('label.whatsapp')} ${t('placeholder.obligatory')}`}
                            value={data.whatsapp}
                            maxLength={10}
                            onChange={(e) => handleContactChange(e)}
                            required
                        />
                        <CFormInput
                            name="mobileNumber"
                            type="text"
                            className="contacts-inputs"
                            label={`${t('label.mobile')}`}
                            value={data.mobileNumber}
                            maxLength={13}
                            onChange={(e) => handleContactChange(e)}
                            required
                        />
                        <CFormInput
                            name="booking"
                            type="text"
                            className="contacts-inputs"
                            label={`${t('label.booking')}`}
                            value={data.booking}
                            onChange={(e) => handleContactChange(e)}
                        />
                    </div>

                    <div className="col-12 col-md-6">

                        <CFormInput
                            name="email"
                            type="text"
                            className="contacts-inputs"
                            label={`${t('label.email')} ${t('placeholder.obligatory')}`}
                            value={data.email}
                            onChange={(e) => handleContactChange(e)}
                            required
                        />
                        <CFormInput
                            name="telephoneNumber"
                            type="text"
                            className="contacts-inputs"
                            label={`${t('label.telephone')}`}
                            value={data.telephoneNumber}
                            maxLength={10}
                            onChange={(e) => handleContactChange(e)}
                        />
                        <CFormInput
                            name="airbnb"
                            type="text"
                            className="contacts-inputs"
                            label={`${t('label.airbnb')}`}
                            value={data.airbnb}
                            onChange={(e) => handleContactChange(e)}
                        />

                    </div>
                    <p>{t("label.contacts_info")}</p>
                    <div className="row g-3 w-100 m-0">
                        <div className="col-12 col-md-6 col-xl-3">
                            <CFormInput
                                name="facebook"
                                type="text"
                                className="contacts-inputs"
                                label={t('label.facebook')}
                                value={data.facebook}
                                onChange={handleContactChange}
                            />
                        </div>

                        <div className="col-12 col-md-6 col-xl-3">
                            <CFormInput
                                name="instagram"
                                type="text"
                                className="contacts-inputs"
                                label={t('label.instagram')}
                                value={data.instagram}
                                onChange={handleContactChange}
                            />
                        </div>

                        <div className="col-12 col-md-6 col-xl-3">
                            <CFormInput
                                name="youtube"
                                type="text"
                                className="contacts-inputs"
                                label={t('label.youtube')}
                                value={data.youtube}
                                onChange={handleContactChange}
                            />
                        </div>

                        <div className="col-12 col-md-6 col-xl-3">
                            <CFormInput
                                name="tiktok"
                                type="text"
                                className="contacts-inputs"
                                label={t('label.tiktok')}
                                value={data.tiktok}
                                onChange={handleContactChange}
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        <CFormInput
                            name="website"
                            type="text"
                            className="contacts-inputs"
                            label={`${t('label.website')}`}
                            value={data.website}
                            onChange={(e) => handleContactChange(e)}
                        />
                    </div>


                    <div className="w-100 d-flex flex-column justify-content-center align-items-center my-3">
                        <div className="w-100 mb-3 d-flex flex-column justify-content-center  align-items-center ">
                            <Button
                                className="btn-add-website"
                                onClick={handleAddRow}
                                disabled={rows.length >= 10} // disabilita se ci sono già 10 siti
                            >
                                {t('button.add_site')}
                            </Button>
                            {rows.map(([key, value], index) => {
                                return (
                                    <div className="d-flex flex-column align-items-center my-3 w-100" key={index}>
                                        <p className="text-start w-100">Sito {index + 1}</p>
                                        <div className="d-flex align-items-center w-100 gap-2">
                                            <div className="d-flex gap-2 flex-grow-1">
                                                <CFormInput
                                                    name={`otherWebsites_key_${index}`}
                                                    type="text"
                                                    className=" w-50"
                                                    value={key}
                                                    onChange={(e) => handleOtherWebsiteChange(e, index, "key")}
                                                />
                                                <CFormInput
                                                    name={`otherWebsites_value_${index}`}
                                                    type="text"
                                                    className="w-50"
                                                    value={value}
                                                    onChange={(e) => handleOtherWebsiteChange(e, index, "value")}
                                                />
                                            </div>
                                            <div className="d-flex justify-content-center align-items-center">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveRow(index)}
                                                    style={{
                                                        background: 'red',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: '25px',
                                                        height: '25px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div
                            className="card-footer bg-transparent w-100"
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: "20px" }}
                        >
                            <Button
                                className="hst-btn-secondary"
                                onClick={() => navigate(-1)}
                            >
                                {t('text.go_back')}
                            </Button>

                            <Button
                                className="text-uppercase hst-btn-primary"
                                onClick={() => handleSave()}
                            //disabled={isDisabled}
                                style={{fontWeight:"500"}}
                            >
                                {t('button.save')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

}